import { generateUuid } from "../../utils/id";
import type { Equality, EqualityUUID, UUID, Vector2, Vector5 } from "../../types/scene";
import { parseSolimp, parseSolref } from "./attributeUtils";
import type { ParsedTendonsMap } from "./types";

type NamedEntity = { name?: string };
type NamedEntityMap = Record<string, NamedEntity | undefined>;
type EqualityParser = {
  xmlDoc: Document;
  equalityDefaultScope: { solref?: Vector2; solimp?: Vector5 };
  equalityClassDefaults: Record<string, { solref?: Vector2; solimp?: Vector5 } | undefined>;
  warn: (message: string) => void;
};

export const parseEqualities = (
  rawParser: unknown,
  bodiesMap: NamedEntityMap,
  jointsMap: NamedEntityMap,
  sitesMap: NamedEntityMap,
  tendonsMap: ParsedTendonsMap
): Record<EqualityUUID, Equality> => {
  const parser = rawParser as EqualityParser;
  const equalities: Record<EqualityUUID, Equality> = {} as Record<EqualityUUID, Equality>;
  // Use "mujoco > equality" to avoid matching <equality> elements inside <default> sections.
  // Collect all sections; include expansion can produce multiple <equality> blocks.
  const equalityRoots = Array.from(
    parser.xmlDoc.querySelectorAll("mujoco > equality")
  ) as Element[];
  if (equalityRoots.length === 0) return equalities;

  const resolveEqualityDefaults = (elem: Element) => {
    const className = elem.getAttribute("class");
    return {
      ...parser.equalityDefaultScope,
      ...(className ? parser.equalityClassDefaults[className] || {} : {}),
    };
  };

  // Helper to find UUID by name for each object type
  const findUuidByName = (name: string | null | undefined, objtype: string): UUID | undefined => {
    if (!name) return undefined;

    let map: NamedEntityMap;
    switch (objtype) {
      case "body":
        map = bodiesMap;
        break;
      case "joint":
        map = jointsMap;
        break;
      case "site":
        map = sitesMap;
        break;
      case "tendon":
        map = tendonsMap;
        break;
      default:
        return undefined;
    }

    // Search for object by name
    for (const [uuid, obj] of Object.entries(map)) {
      if (obj && obj.name === name) {
        return uuid as UUID;
      }
    }

    parser.warn(`[MJCFParser] Could not find UUID for ${objtype} named "${name}"`);
    return undefined;
  };

  for (const equalityRoot of equalityRoots) {
    // Parse connect constraints
    equalityRoot.querySelectorAll("connect").forEach((connect: Element) => {
      const name = connect.getAttribute("name") || undefined;
      const body1 = connect.getAttribute("body1");
      const body2 = connect.getAttribute("body2");
      const site1 = connect.getAttribute("site1");
      const site2 = connect.getAttribute("site2");
      const anchor = connect.getAttribute("anchor");
      const active = connect.getAttribute("active");
      const solref = connect.getAttribute("solref");
      const solimp = connect.getAttribute("solimp");
      const equalityDefaults = resolveEqualityDefaults(connect);

      // Parse anchor as 3D vector if present
      let data: number[] | undefined;
      if (anchor) {
        const anchorVec = anchor
          .trim()
          .split(/\s+/)
          .map(Number)
          .filter((n) => Number.isFinite(n));
        if (anchorVec.length >= 3) {
          data = anchorVec.slice(0, 3);
        }
      }

      // Parse solref as 2D vector if present
      const solrefVec = parseSolref(solref) || equalityDefaults.solref;

      // Parse solimp as 5D vector if present (accepts 3-5 values)
      const solimpVec = parseSolimp(solimp) || equalityDefaults.solimp;

      // Determine object type and convert names to UUIDs
      const objtype = (body1 || body2 ? "body" : "site") as "body" | "site" | undefined;
      const name1Str = body1 || site1 || undefined;
      const name2Str = body2 || site2 || undefined;
      const obj1 = objtype ? findUuidByName(name1Str, objtype) : undefined;
      const obj2 = objtype ? findUuidByName(name2Str, objtype) : undefined;

      const uuid = generateUuid() as EqualityUUID;
      const equality: Equality = {
        uuid,
        name,
        type: "connect" as const,
        obj1,
        obj2,
        objtype,
        data, // anchor point stored in data
        active: active === "true" ? true : active === "false" ? false : undefined,
        solref: solrefVec,
        solimp: solimpVec,
      };

      equalities[uuid] = equality;
    });

    // Parse joint constraints
    equalityRoot.querySelectorAll("joint").forEach((joint: Element) => {
      const name = joint.getAttribute("name") || undefined;
      const joint1 = joint.getAttribute("joint1");
      const joint2 = joint.getAttribute("joint2");
      const polycoef = joint.getAttribute("polycoef");
      const active = joint.getAttribute("active");
      const solref = joint.getAttribute("solref");
      const solimp = joint.getAttribute("solimp");
      const equalityDefaults = resolveEqualityDefaults(joint);

      // Parse polycoef as array if present
      let data: number[] | undefined;
      if (polycoef) {
        const polycoefNums = polycoef
          .trim()
          .split(/\s+/)
          .map(Number)
          .filter((n) => Number.isFinite(n));
        if (polycoefNums.length === 5) {
          data = polycoefNums;
        } else if (polycoefNums.length > 0) {
          parser.warn(
            `[mjcfParser] Replacing invalid joint equality polycoef for "${name || "unnamed equality"}" with identity coefficients. MuJoCo requires exactly 5 values.`
          );
          data = [0, 1, 0, 0, 0];
        }
      }

      // Parse solref as 2D vector if present
      const solrefVec = parseSolref(solref) || equalityDefaults.solref;

      // Parse solimp as 5D vector if present (accepts 3-5 values)
      const solimpVec = parseSolimp(solimp) || equalityDefaults.solimp;

      // Convert joint names to UUIDs
      const obj1 = findUuidByName(joint1, "joint");
      const obj2 = findUuidByName(joint2, "joint");

      const uuid = generateUuid() as EqualityUUID;
      const equality: Equality = {
        uuid,
        name,
        type: "joint" as const,
        obj1,
        obj2,
        objtype: "joint" as const,
        data,
        active: active === "true" ? true : active === "false" ? false : undefined,
        solref: solrefVec,
        solimp: solimpVec,
      };

      equalities[uuid] = equality;
    });

    // Parse weld constraints
    equalityRoot.querySelectorAll("weld").forEach((weld: Element) => {
      const name = weld.getAttribute("name") || undefined;
      const body1 = weld.getAttribute("body1");
      const body2 = weld.getAttribute("body2");
      const relpose = weld.getAttribute("relpose");
      const torquescaleAttr = weld.getAttribute("torquescale");
      const active = weld.getAttribute("active");
      const solref = weld.getAttribute("solref");
      const solimp = weld.getAttribute("solimp");
      const equalityDefaults = resolveEqualityDefaults(weld);

      // Parse relpose as 7D vector (pos + quat) if present
      let data: number[] | undefined;
      if (relpose) {
        const relposeNums = relpose
          .trim()
          .split(/\s+/)
          .map(Number)
          .filter((n) => Number.isFinite(n));
        if (relposeNums.length >= 7) {
          data = relposeNums.slice(0, 7);
        }
      }

      // Parse solref as 2D vector if present
      const solrefVec = parseSolref(solref) || equalityDefaults.solref;

      // Parse solimp as 5D vector if present (accepts 3-5 values)
      const solimpVec = parseSolimp(solimp) || equalityDefaults.solimp;
      const torquescale = torquescaleAttr != null ? Number(torquescaleAttr) : undefined;

      // Convert body names to UUIDs
      const obj1 = findUuidByName(body1, "body");
      const obj2 = findUuidByName(body2, "body");

      const uuid = generateUuid() as EqualityUUID;
      const equality: Equality = {
        uuid,
        name,
        type: "weld" as const,
        obj1,
        obj2,
        objtype: "body" as const,
        data,
        active: active === "true" ? true : active === "false" ? false : undefined,
        solref: solrefVec,
        solimp: solimpVec,
        ...(Number.isFinite(torquescale) ? { torquescale } : {}),
      };

      equalities[uuid] = equality;
    });

    // Parse tendon constraints
    equalityRoot.querySelectorAll("tendon").forEach((tendon: Element) => {
      const name = tendon.getAttribute("name") || undefined;
      const tendon1 = tendon.getAttribute("tendon1");
      const tendon2 = tendon.getAttribute("tendon2");
      const polycoef = tendon.getAttribute("polycoef");
      const active = tendon.getAttribute("active");
      const solref = tendon.getAttribute("solref");
      const solimp = tendon.getAttribute("solimp");
      const equalityDefaults = resolveEqualityDefaults(tendon);

      // Parse polycoef as array if present
      let data: number[] | undefined;
      if (polycoef) {
        const polycoefNums = polycoef
          .trim()
          .split(/\s+/)
          .map(Number)
          .filter((n) => Number.isFinite(n));
        if (polycoefNums.length === 5) {
          data = polycoefNums;
        } else if (polycoefNums.length > 0) {
          parser.warn(
            `[mjcfParser] Replacing invalid tendon equality polycoef for "${name || "unnamed equality"}" with identity coefficients. MuJoCo requires exactly 5 values.`
          );
          data = [0, 1, 0, 0, 0];
        }
      }

      // Parse solref as 2D vector if present
      const solrefVec = parseSolref(solref) || equalityDefaults.solref;

      // Parse solimp as 5D vector if present (accepts 3-5 values)
      const solimpVec = parseSolimp(solimp) || equalityDefaults.solimp;

      // Convert tendon names to UUIDs
      // Note: Tendons may not be implemented yet, so UUID lookup might return undefined
      const obj1 = findUuidByName(tendon1, "tendon");
      const obj2 = findUuidByName(tendon2, "tendon");

      const uuid = generateUuid() as EqualityUUID;
      const equality: Equality = {
        uuid,
        name,
        type: "tendon" as const,
        obj1,
        obj2,
        objtype: "tendon" as const,
        data,
        active: active === "true" ? true : active === "false" ? false : undefined,
        solref: solrefVec,
        solimp: solimpVec,
      };

      equalities[uuid] = equality;
    });
  } // end equalityRoots loop

  return equalities;
};
